// src/utils/sicCodes.ts

/**
 * A comprehensive mapping of 2-digit UK SIC 2007 section codes to their descriptions.
 * This provides broader, more user-friendly industry names.
 * Source: Condensed list from UK Government Companies House.
 */
export const SIC_CODE_DESCRIPTIONS: { [key: string]: string } = {
    '01': 'Agriculture, Forestry and Fishing',
    '02': 'Forestry and Logging',
    '03': 'Fishing and Aquaculture',
    '05': 'Mining of Coal and Lignite',
    '06': 'Extraction of Crude Petroleum and Natural Gas',
    '07': 'Mining of Metal Ores',
    '08': 'Other Mining and Quarrying',
    '09': 'Mining Support Service Activities',
    '10': 'Manufacture of Food Products',
    '11': 'Manufacture of Beverages',
    '12': 'Manufacture of Tobacco Products',
    '13': 'Manufacture of Textiles',
    '14': 'Manufacture of Wearing Apparel',
    '15': 'Manufacture of Leather and Related Products',
    '16': 'Manufacture of Wood and of Products of Wood and Cork',
    '17': 'Manufacture of Paper and Paper Products',
    '18': 'Printing and Reproduction of Recorded Media',
    '19': 'Manufacture of Coke and Refined Petroleum Products',
    '20': 'Manufacture of Chemicals and Chemical Products',
    '21': 'Manufacture of Basic Pharmaceutical Products',
    '22': 'Manufacture of Rubber and Plastic Products',
    '23': 'Manufacture of Other Non-Metallic Mineral Products',
    '24': 'Manufacture of Basic Metals',
    '25': 'Manufacture of Fabricated Metal Products',
    '26': 'Manufacture of Computer, Electronic and Optical Products',
    '27': 'Manufacture of Electrical Equipment',
    '28': 'Manufacture of Machinery and Equipment n.e.c.',
    '29': 'Manufacture of Motor Vehicles, Trailers and Semi-Trailers',
    '30': 'Manufacture of Other Transport Equipment',
    '31': 'Manufacture of Furniture',
    '32': 'Other Manufacturing',
    '33': 'Repair and Installation of Machinery and Equipment',
    '35': 'Electricity, Gas, Steam and Air Conditioning Supply',
    '36': 'Water Collection, Treatment and Supply',
    '37': 'Sewerage',
    '38': 'Waste Collection, Treatment and Disposal Activities',
    '39': 'Remediation Activities and Other Waste Management Services',
    '41': 'Construction of Buildings',
    '42': 'Civil Engineering',
    '43': 'Specialised Construction Activities',
    '45': 'Wholesale and Retail Trade and Repair of Motor Vehicles',
    '46': 'Wholesale Trade, Except of Motor Vehicles',
    '47': 'Retail Trade, Except of Motor Vehicles',
    '49': 'Land Transport and Transport via Pipelines',
    '50': 'Water Transport',
    '51': 'Air Transport',
    '52': 'Warehousing and Support Activities for Transportation',
    '53': 'Postal and Courier Activities',
    '55': 'Accommodation',
    '56': 'Food and Beverage Service Activities',
    '58': 'Publishing Activities',
    '59': 'Motion Picture, Video and Television Programme Production',
    '60': 'Programming and Broadcasting Activities',
    '61': 'Telecommunications',
    '62': 'Computer Programming, Consultancy and Related Activities',
    '63': 'Information Service Activities',
    '64': 'Financial Service Activities, Except Insurance',
    '65': 'Insurance, Reinsurance and Pension Funding',
    '66': 'Activities Auxiliary to Financial Services and Insurance',
    '68': 'Real Estate Activities',
    '69': 'Legal and Accounting Activities',
    '70': 'Activities of Head Offices; Management Consultancy',
    '71': 'Architectural and Engineering Activities',
    '72': 'Scientific Research and Development',
    '73': 'Advertising and Market Research',
    '74': 'Other Professional, Scientific and Technical Activities',
    '75': 'Veterinary Activities',
    '77': 'Rental and Leasing Activities',
    '78': 'Employment Activities',
    '79': 'Travel Agency, Tour Operator and Other Reservation Service',
    '80': 'Security and Investigation Activities',
    '81': 'Services to Buildings and Landscape Activities',
    '82': 'Office Administrative, Office Support and Other Business Support Activities',
    '84': 'Public Administration and Defence',
    '85': 'Education',
    '86': 'Human Health Activities',
    '87': 'Residential Care Activities',
    '88': 'Social Work Activities Without Accommodation',
    '90': 'Creative, Arts and Entertainment Activities',
    '91': 'Libraries, Archives, Museums and Other Cultural Activities',
    '92': 'Gambling and Betting Activities',
    '93': 'Sports Activities and Amusement and Recreation Activities',
    '94': 'Activities of Membership Organisations',
    '95': 'Repair of Computers and Personal and Household Goods',
    '96': 'Other Personal Service Activities',
    '97': 'Activities of Households as Employers of Domestic Personnel',
    '98': 'Activities of Households for Own Use',
    '99': 'Activities of Extraterritorial Organisations and Bodies',
};

/**
 * Ireland industry sector mappings
 * Maps Ireland's text-based industry codes to display names
 */
export const IRELAND_INDUSTRY_NAMES: { [key: string]: string } = {
    'Manufacturing': 'Manufacturing',
    'Public Administration and Defence Compulsory Social Security': 'Public Administration and Defence',
    'Telecommunication Computer Programming Consulting Computing Infrastructure and Other Information Service Activities': 'Telecommunications and IT Services',
    'Financial and Insurance Activities': 'Financial and Insurance Activities',
    'Wholesale and Retail Trade': 'Wholesale and Retail Trade',
    'Human Health and Social Work Activities': 'Human Health and Social Work',
    'Professional Scientific and Technical Activities': 'Professional, Scientific and Technical',
    'Unknown': 'Unknown',
    'Accommodation and Food Service Activities': 'Accommodation and Food Services',
    'Education': 'Education',
    'Administrative and Support Service Activities': 'Administrative and Support Services',
    'Construction': 'Construction',
    'Transportation and Storage': 'Transportation and Storage',
    'Arts Sports and Recreation': 'Arts, Sports and Recreation',
    'Other Service Activities': 'Other Services',
    'Publishing Broadcasting and Content Production and Distribution Activites': 'Media and Broadcasting',
    'Electricity Gas Steam and Air Conditioning Supply': 'Utilities',
    'Agriculture Forestry and Fishing': 'Agriculture, Forestry and Fishing',
    'Real Estate Activites': 'Real Estate',
    'Water Supply Sewerage Waste Management and Remediation Activities': 'Water and Waste Management',
    'Mining and Quarrying': 'Mining and Quarrying',
};

export const SIC_CODE_ABBREVIATIONS: { [key: string]: string } = {
    '01': 'Agriculture',
    '02': 'Forestry',
    '03': 'Fishing',
    '05': 'Mining',
    '06': 'Oil & Gas',
    '07': 'Mining',
    '08': 'Quarrying',
    '09': 'Mining Support',
    '10': 'Food Products',
    '11': 'Beverages',
    '12': 'Tobacco',
    '13': 'Textiles',
    '14': 'Apparel',
    '15': 'Leather',
    '16': 'Wood Products',
    '17': 'Paper Products',
    '18': 'Printing',
    '19': 'Refined Petroleum',
    '20': 'Chemicals',
    '21': 'Pharmaceuticals',
    '22': 'Rubber & Plastics',
    '23': 'Non-Metallic Minerals',
    '24': 'Basic Metals',
    '25': 'Fabricated Metals',
    '26': 'Electronics',
    '27': 'Electrical Equipment',
    '28': 'Machinery',
    '29': 'Motor Vehicles',
    '30': 'Transport Equipment',
    '31': 'Furniture',
    '32': 'Manufacturing',
    '33': 'Repair & Installation',
    '35': 'Utilities',
    '36': 'Water Supply',
    '37': 'Sewerage',
    '38': 'Waste Management',
    '39': 'Remediation',
    '41': 'Construction',
    '42': 'Civil Engineering',
    '43': 'Construction',
    '45': 'Automotive Trade',
    '46': 'Wholesale Trade',
    '47': 'Retail Trade',
    '49': 'Land Transport',
    '50': 'Water Transport',
    '51': 'Air Transport',
    '52': 'Warehousing',
    '53': 'Postal & Courier',
    '55': 'Accommodation',
    '56': 'Food & Beverage',
    '58': 'Publishing',
    '59': 'Media Production',
    '60': 'Broadcasting',
    '61': 'Telecommunications',
    '62': 'IT & Programming',
    '63': 'Information Services',
    '64': 'Financial Services',
    '65': 'Insurance & Pensions',
    '66': 'Financial Support',
    '68': 'Real Estate',
    '69': 'Legal & Accounting',
    '70': 'Consultancy',
    '71': 'Engineering',
    '72': 'R&D',
    '73': 'Advertising',
    '74': 'Professional Services',
    '75': 'Veterinary',
    '77': 'Rental & Leasing',
    '78': 'Employment',
    '79': 'Travel',
    '80': 'Security',
    '81': 'Building Services',
    '82': 'Business Support',
    '84': 'Public Admin',
    '85': 'Education',
    '86': 'Healthcare',
    '87': 'Residential Care',
    '88': 'Social Work',
    '90': 'Creative & Arts',
    '91': 'Cultural Activities',
    '92': 'Gambling',
    '93': 'Sports & Recreation',
    '94': 'Membership Orgs',
    '95': 'Repair Services',
    '96': 'Personal Services',
    '97': 'Domestic Personnel',
    '98': 'Household Activities',
    '99': 'Extraterritorial Orgs',
};

const getSectionCode = (code: string | number | null): string | null => {
    if (code === null || code === undefined) return null;

    // If it's a text-based code (Ireland), return as-is
    const codeStr = String(code);
    if (isNaN(Number(codeStr)) && codeStr !== 'NO_SIC') {
        return codeStr;
    }

    // For numeric codes (UK/France), extract first 2 digits
    return String(parseInt(codeStr, 10)).padStart(2, '0').substring(0, 2);
};

export const getIndustryName = (code: string | number | null): string => {
    if (code === null || code === undefined) return 'Unknown';

    const codeStr = String(code);

    // Check if it's an Ireland text-based code
    if (isNaN(Number(codeStr)) && codeStr !== 'NO_SIC') {
        return IRELAND_INDUSTRY_NAMES[codeStr] || codeStr;
    }

    // For numeric codes (UK/France), use SIC code descriptions
    const sectionCode = getSectionCode(code);
    return sectionCode ? (SIC_CODE_DESCRIPTIONS[sectionCode] || 'Unknown') : 'Unknown';
};

export const getIndustryAbbreviation = (code: string | number | null): string => {
    if (code === null || code === undefined) return 'Unknown';

    const codeStr = String(code);

    // Check if it's an Ireland text-based code
    if (isNaN(Number(codeStr)) && codeStr !== 'NO_SIC') {
        // Return abbreviated version for Ireland codes
        return IRELAND_INDUSTRY_NAMES[codeStr] || codeStr;
    }

    // For numeric codes (UK/France), use SIC abbreviations
    const sectionCode = getSectionCode(code);
    return sectionCode ? (SIC_CODE_ABBREVIATIONS[sectionCode] || 'Unknown') : 'Unknown';
};
